// WaitingRoom.jsx
// Reusable "please wait" panel for the app's long-running backend work (the
// lead scrub, and the import batches). Shows progress — or an indeterminate
// bar when there's nothing to measure — plus an optional arcade game so the
// wait doesn't feel like dead time.
//
// When the work finishes the panel does NOT disappear on its own: callers
// pass `done` + `doneMessage` and the panel swaps to a "Done — Continue"
// button instead of yanking itself (and the game) away. The caller only
// renders its results view after `onContinue` fires.
import React, { useEffect, useRef } from "react";
import RunnerGame from "./arcade/RunnerGame";
import "./WaitingRoom.css";

export default function WaitingRoom({
	active = false,
	done = false,
	title = "Working…",
	progress = null, // optional { value, max }
	doneMessage = "",
	onContinue,
}) {
	const continueRef = useRef(null);

	// Accessibility: as soon as the work finishes, move focus to Continue so
	// keyboard/screen-reader users aren't left stranded on the game canvas.
	useEffect(() => {
		if (done && continueRef.current) {
			continueRef.current.focus();
		}
	}, [done]);

	if (!active && !done) return null;

	const hasProgress = Boolean(progress && progress.max > 0);
	const pct = hasProgress
		? Math.min(100, Math.round((progress.value / progress.max) * 100))
		: null;

	return (
		<div className="waitingRoom">
			{/* aria-live so progress/done updates get announced without the
			    user needing to re-focus this panel. */}
			<div className="waitingRoomStatus" role="status" aria-live="polite">
				{!done && (
					<>
						<h3 className="waitingRoomTitle">{title}</h3>
						<div
							className="waitingRoomProgress"
							title={hasProgress ? `${progress.value} / ${progress.max}` : undefined}>
							<div
								className={`waitingRoomProgressInner ${hasProgress ? "" : "indeterminate"}`}
								style={hasProgress ? { width: `${pct}%` } : undefined}
							/>
							<span className="waitingRoomProgressText">
								{hasProgress
									? `${progress.value} / ${progress.max} (${pct}%)`
									: "Working…"}
							</span>
						</div>
					</>
				)}
				{done && <p className="waitingRoomDoneMessage">{doneMessage || "Done."}</p>}
			</div>

			{/* Purely optional distraction — nobody has to touch it. The
			    Continue button below always works whether or not they play. */}
			<div className="waitingRoomGameWrap">
				<RunnerGame />
			</div>

			{done && (
				<div className="waitingRoomActions">
					<button
						type="button"
						ref={continueRef}
						className="waitingRoomContinueBtn"
						onClick={onContinue}>
						Done — Continue
					</button>
				</div>
			)}
		</div>
	);
}
